import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useState } from "react";
import { NavLink as Link } from "react-router-dom";
import { endpoints } from "../../hooks/endpoints";
import { getToken } from "../../hooks/utils";
import { useAppSelector } from "../../store/hooks";
import Animation from "../Animation";
import Spinner from "../Spinner";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";

const Invoices = () => {
  const [invoicesStart, setInvoicesStart] = useState(0);
  const { currentTeacher } = useAppSelector((state) => state.store);

  const fetchInvoices = async () => {
    try {
      const res = await (
        await fetch(`${endpoints.invoices}?teacher=${currentTeacher}`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: getToken(),
          },
          method: "GET",
        })
      ).json();
      return res.filter(
        (invoice) =>
          invoice.is_wallet_recharge ||
          invoice.is_codes_purchase ||
          invoice.cat_slug === currentTeacher
      );
    } catch (err) {
      console.log(err);
      return [];
    }
  };

  const {
    data: invoices,
    isPending,
    error,
  } = useQuery({
    queryKey: ["invoices"],
    queryFn: fetchInvoices,
    staleTime: 5 * 60 * 1000,
  });

  // let invoices = [];

  // try {
  //   invoices =
  //     data?.status !== 404
  //       ? data?.filter(
  //           (invoice) =>
  //             invoice.is_wallet_recharge ||
  //             invoice.cat_slug.includes(currentTeacher)
  //         )
  //       : [];
  // } catch (error) {
  //   console.log(error);
  // }

  if (isPending) {
    return (
      <div className="grow w-full flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (!invoices || invoices?.length < 1 || invoices?.status === 404) {
    return (
      <Animation expand={false}>
        <div className="w-full flex justify-center">
          <p className="p-3 my-12 bg-red-50 border border-red-400 rounded-xl text-red-700 text-xl">
            لا يوجد مشتريات
          </p>
        </div>
      </Animation>
    );
  }

  return (
    <Animation expand={false}>
      <div className="max-w-4xl w-full mx-auto px-4 py-4">
        {/* <h2 className="text-primary text-3xl font-bold mb-8 mt-6">مشترياتي</h2> */}

        <Card className="mt-8">
          <CardHeader>
            <CardTitle className="font-bold text-2xl">
              تفاصيل المشتريات
            </CardTitle>
          </CardHeader>

          <CardContent className="overflow-x-auto">
            {invoices && (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-start font-bold text-gray-500">
                      رقم العملية
                    </TableHead>
                    <TableHead className="text-start font-bold text-gray-500">
                      التاريخ
                    </TableHead>
                    <TableHead className="text-start font-bold text-gray-500">
                      الحالة
                    </TableHead>
                    <TableHead className="text-start font-bold text-gray-500">
                      الإجمالي
                    </TableHead>
                    <TableHead className="text-start font-bold text-gray-500">
                      تفاصيل
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoices
                    .slice(invoicesStart, invoicesStart + 10)
                    .map((invoice) => (
                      <TableRow key={invoice.id}>
                        <TableCell className="font-medium">
                          {invoice.id}
                        </TableCell>
                        <TableCell>{invoice.date}</TableCell>
                        <TableCell>
                          {invoice.status === "completed" ? (
                            <Badge variant="default" className="bg-green-600">
                              تم الدفع
                            </Badge>
                          ) : invoice.status === "pending" ? (
                            <Badge variant="destructive">منتظر الدفع</Badge>
                          ) : (
                            <Badge>{invoice.status}</Badge>
                          )}
                        </TableCell>
                        <TableCell>{invoice.total} جنيه</TableCell>
                        <TableCell>
                          <Link
                            to={`/purchases/${invoice.id}`}
                            className="inline-flex items-center justify-center px-3 py-1 rounded-lg bg-primary text-white text-sm hover:bg-primary/90 transition"
                          >
                            تفاصيل
                          </Link>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            )}

            {/* Pagination */}
            <div className="flex items-center justify-between mt-4">
              <p className="text-sm text-gray-600">
                عرض {invoicesStart + 1} إلى{" "}
                {Math.min(invoicesStart + 10, invoices.length)} من{" "}
                {invoices.length} نتيجة
              </p>
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  disabled={invoicesStart <= 0}
                  size="sm"
                  onClick={() =>
                    setInvoicesStart((prev) => Math.max(prev - 10, 0))
                  }
                >
                  <ChevronRight className="w-4 h-4 ml-1" />
                  السابق
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={invoicesStart + 10 >= invoices.length}
                  onClick={() => setInvoicesStart((prev) => prev + 10)}
                >
                  التالي
                  <ChevronLeft className="w-4 h-4 mr-1" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Animation>
  );

  return (
    <Animation expand={false}>
      <div className="container responsive-pages py-3">
        <h2 className="p-3 my-2 bg-primary rounded-ea text-white">
          مشترياتي
        </h2>
        <div className="table-responsive bg-white responsive-padding-pages-containers rounded-ea shadow">
          {invoices && (
            <Animation expand={false}>
              <table className="table table-striped">
                <thead>
                  <tr>
                    <th scope="col">رقم العملية</th>
                    <th scope="col">التاريخ</th>
                    <th scope="col">الحالة</th>
                    <th scope="col">الإجمالي</th>
                    <th scope="col">تفاصيل</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices &&
                    invoices.map(
                      (invoice, index) =>
                        index >= invoicesStart &&
                        index < invoicesStart + 10 && (
                          <tr>
                            <th scope="row">{invoice.id}</th>
                            <td>{invoice.date}</td>
                            <td>
                              {invoice.status === "completed" ? (
                                <p className="text-success fw-bold">تم الدفع</p>
                              ) : invoice.status === "pending" ? (
                                <p className="text-danger fw-bold">
                                  منتظر الدفع
                                </p>
                              ) : (
                                invoice.status
                              )}
                            </td>
                            <td>{invoice.total} جنيه</td>
                            <td>
                              <Link
                                to={`/purchases/${invoice.id}`}
                                style={{
                                  maxHeight: "30px",
                                  padding: "3px 8px",
                                }}
                                className="pt-0 rounded-ea"
                              >
                                تفاصيل
                              </Link>
                            </td>
                          </tr>
                        )
                    )}
                </tbody>
              </table>
            </Animation>
          )}
          <div className="d-flex">
            {invoices && invoicesStart + 10 < invoices.length && (
              <button
                onClick={() => setInvoicesStart((prev) => prev + 10)}
                className="m-1 rounded-ea"
              >
                التالي
              </button>
            )}
            {invoices && invoicesStart > 0 && (
              <button
                onClick={() => setInvoicesStart((prev) => prev - 10)}
                className="m-1 rounded-ea"
              >
                السابق
              </button>
            )}
          </div>
        </div>
      </div>
    </Animation>
  );
};

export default Invoices;
